/**
 * Base GitHub API client
 */
class GitHubClient {
  constructor(accessToken = '') {
    this.accessToken = accessToken;
    this.baseUrl = 'https://api.github.com';
  }

  /**
   * Create request headers with optional authorization
   * @returns {Object}
   */
  getHeaders() {
    const headers = {
      'Accept': 'application/vnd.github+json'
    };
    
    if (this.accessToken) {
      headers['Authorization'] = `token ${this.accessToken}`;
    }
    
    return headers;
  }

  /**
   * Fetch branches for a repository
   * @param {string} nameAndRepo - Repository path in format /owner/repo
   * @returns {Promise<string[]>}
   */
  async getBranches(nameAndRepo) {
    const response = await fetch(
      `${this.baseUrl}/repos${nameAndRepo}/branches`,
      { headers: this.getHeaders() }
    );
    const data = await response.json();
    
    if (data.message) {
      throw new Error(data.message);
    }
    
    return data.map(branch => branch.name);
  }

  /**
   * Fetch file tree recursively
   * @param {string} nameAndRepo - Repository path
   * @param {string} branch - Branch name
   * @returns {Promise<Array>}
   */
  async getFileTree(nameAndRepo, branch) {
    const response = await fetch(
      `${this.baseUrl}/repos${nameAndRepo}/git/trees/${branch}?recursive=1`,
      { headers: this.getHeaders() }
    );
    const data = await response.json();
    
    if (data.message) {
      throw new Error(data.message);
    }
    
    return data.tree || [];
  }

  /**
   * Fetch file contents
   * @param {string} nameAndRepo - Repository path
   * @param {string} filePath - File path in repository
   * @param {string} branch - Branch name
   * @returns {Promise<string>} - Base64 encoded content
   */
  async getFileContent(nameAndRepo, filePath, branch) {
    const response = await fetch(
      `${this.baseUrl}/repos${nameAndRepo}/contents/${filePath}?ref=${branch}`,
      { headers: this.getHeaders() }
    );
    const data = await response.json();
    
    if (data.message) {
      throw new Error(data.message);
    }

    if (Array.isArray(data)) {
      throw new Error('Target path is a directory, not a file.');
    }
    
    return data.content;
  }
}

export default GitHubClient;
